import { TaskLanguageSelect, TaskNotice, TaskSetup } from '@/features/contribution-kit';
import { WriteModeSwitch } from '@/components/contribute/WriteModeSwitch';
import type { TranscriptionPhraseCopy } from './transcription-phrase-copy';

type TranscriptionPhraseSetupProps = {
  t: Record<string, unknown>;
  copy: TranscriptionPhraseCopy;
  title: string;
  description: string;
  showLanguageSelect: boolean;
  showModeSwitch: boolean;
  languages: Array<{ code: string; name: string; displayName?: string }>;
  selectedLanguage: string;
  onLanguageChange: (code: string) => void;
  sessionResumeActive: boolean;
  isLoading: boolean;
  onStart: () => void;
  onBack: () => void;
};

/** Before a "Traduire" session: writing mode, target language, rules, start. */
export function TranscriptionPhraseSetup({
  t,
  copy,
  title,
  description,
  showLanguageSelect,
  showModeSwitch,
  languages,
  selectedLanguage,
  onLanguageChange,
  sessionResumeActive,
  isLoading,
  onStart,
  onBack,
}: TranscriptionPhraseSetupProps) {
  const s = (key: string) => String(t[key] ?? '');
  return (
    <TaskSetup
      task="translate"
      title={title}
      description={description}
      instructions={[...copy.instructions]}
      notices={
        sessionResumeActive && selectedLanguage ? (
          <TaskNotice tone="info" title={copy.resumeTitle}>
            {copy.resumeBody}
          </TaskNotice>
        ) : null
      }
      startLabel={s('beginSession')}
      onStart={onStart}
      startDisabled={!selectedLanguage}
      starting={isLoading}
      onBack={onBack}
    >
      {showModeSwitch ? <WriteModeSwitch active="translate" className="w-full sm:grid" /> : null}
      {showLanguageSelect ? (
        <div className="space-y-2">
          <label htmlFor="transcription-phrase-language" className="text-sm font-medium text-foreground">
            {s('selectLanguage')}
          </label>
          <p className="text-sm leading-6 text-muted-foreground">{s('prepareTranslationDesc')}</p>
          <TaskLanguageSelect
            id="transcription-phrase-language"
            value={selectedLanguage}
            onChange={onLanguageChange}
            languages={languages.map((language) => ({ ...language, flag: undefined }))}
            placeholder={s('selectLanguage')}
          />
        </div>
      ) : null}
    </TaskSetup>
  );
}
